import { useMemo, useState } from 'react';

import Button from '@liquidweb-dev/undercurrent/Button';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import CardLayered from '@liquidweb-dev/undercurrent/CardLayered';
import CardLayeredHeader from '@liquidweb-dev/undercurrent/CardLayeredHeader';
import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogActions from '@liquidweb-dev/undercurrent/DialogActions';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import Fade from '@liquidweb-dev/undercurrent/Fade';
import InfoIcon from '@liquidweb-dev/undercurrent/icons/lucide/Info';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import toast from '@liquidweb-dev/undercurrent/toast';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import HookedSubmit from 'components/standard/Form/HookedSubmit';
import HookedTextField from 'components/standard/Form/HookedTextField';
import LWForm from 'components/LWForm';

import useCreateIncoming from 'modules/queries/network/dns/domain/transfer/useCreateIncoming';
import useAssetNetworkDnsDomainTldDetails from 'modules/queries/network/dns/domain/useTldDetails';
import useDebounce from 'utility/effects/useDebounce';

const initialFormErrors = {
	domain: '',
	eppCode: '',
};

export interface DialogTransferDomainProps
	extends Omit<DialogProps, 'onClose'> {
	onClose: () => void;
	onSuccess?: () => void;
	initialDomain?: string;
}

const DialogTransferDomain = ({
	onClose,
	onSuccess,
	initialDomain,
	...dialogProps
}: DialogTransferDomainProps) => {
	const initialFormValues = {
		domain: initialDomain || '',
		eppCode: '',
	};
	const [formValues, setFormValues] = useState(initialFormValues);
	const [formErrors, setFormErrors] = useState(initialFormErrors);

	const debouncedDomain = useDebounce(formValues.domain, 500);
	const {
		data: tldDetailsData,
		isLoading: isTldDetailsLoading,
		isError: isTldDetailsError,
	} = useAssetNetworkDnsDomainTldDetails(
		{ domains: debouncedDomain },
		{ enabled: Boolean(debouncedDomain) && debouncedDomain !== '' },
	);

	const { mutate: createIncoming, isLoading: isCreateIncomingLoading } =
		useCreateIncoming();

	const tldDetails = useMemo(() => {
		if (!tldDetailsData) return null;
		return Object.values(tldDetailsData)[0];
	}, [tldDetailsData]);

	const handleClose = () => {
		setFormValues(initialFormValues);
		setFormErrors(initialFormErrors);
		onClose();
	};

	const handleSubmit = () => {
		const { domain, eppCode } = formValues;
		createIncoming(
			{ domain, eppCode },
			{
				onSuccess: () => {
					onSuccess?.();
					toast.success('Transfer initiated');
					handleClose();
				},
				onError: () => {
					toast.danger('Failed to initiate transfer');
				},
			},
		);
	};

	const disableSubmit =
		isTldDetailsLoading ||
		isTldDetailsError ||
		!formValues.domain ||
		!formValues.eppCode;

	return (
		<Dialog onClose={handleClose} {...dialogProps}>
			<DialogTitle onClose={handleClose}>Transfer a domain</DialogTitle>
			<LWForm
				values={formValues}
				onValuesChange={setFormValues}
				errors={formErrors}
				onErrorsChange={setFormErrors}
				onSubmit={handleSubmit}
				validators={{
					domain: () => {
						if (isTldDetailsError) return 'Please enter a valid domain name';
						return '';
					},
				}}
			>
				<DialogContent>
					<Stack spacing={300}>
						<Stack spacing={200}>
							<HookedTextField
								name="domain"
								label="Domain to transfer"
								placeholder="Enter domain"
								disabled={isCreateIncomingLoading}
								required
							/>
							<HookedTextField
								name="eppCode"
								label="EEP code"
								placeholder="Enter transfer code"
								disabled={isCreateIncomingLoading}
								required
							/>
						</Stack>

						<CardLayered
							bgcolor={({ vars }) => vars.palette.uc.highlight.bg.main}
						>
							<CardLayeredHeader
								bgcolor={({ vars }) => vars.palette.uc.highlight.bg.main}
							>
								<Stack direction="row" spacing={100} alignItems="center">
									<InfoIcon />
									<Typography variant="label-lg">
										Includes one-year renewal at the standard registration price
									</Typography>
								</Stack>
							</CardLayeredHeader>
							<Card
								sx={({ vars }) => ({
									borderColor: vars.palette.uc.highlight.border.weak,
								})}
							>
								<CardContent>
									<Stack spacing={150}>
										<Stack
											direction="row"
											spacing={100}
											alignItems="center"
											justifyContent="space-between"
										>
											<Typography variant="body-sm" fontWeight={500}>
												Price you pay at transfer
											</Typography>

											{(!isTldDetailsLoading || formValues.domain === '') && (
												<Fade in={true} timeout={300}>
													<Typography
														variant="body-sm"
														sx={({ vars }) => ({
															color: tldDetails?.transfer_price
																? vars.palette.uc.success.text.main
																: vars.palette.uc.text.weak,
														})}
													>
														{tldDetails?.transfer_price
															? `$${tldDetails.transfer_price}`
															: '—'}
													</Typography>
												</Fade>
											)}
											{isTldDetailsLoading && formValues.domain !== '' && (
												<Fade in={true} timeout={300}>
													<Skeleton variant="text" width={100} height={24} />
												</Fade>
											)}
										</Stack>
										<Typography
											variant="body-sm"
											sx={({ vars }) => ({ color: vars.palette.uc.text.weak })}
										>
											If you have a card on file, we&apos;ll authorize the
											amount and charge it when the transfer completes. If not,
											you can add funds to your account balance to cover the
											transfer in Account Settings.
										</Typography>
									</Stack>
								</CardContent>
							</Card>
						</CardLayered>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'flex-end' }}>
					<Button
						variant="secondary"
						size="large"
						onClick={handleClose}
						disabled={isCreateIncomingLoading}
						fullWidth
					>
						Cancel
					</Button>
					<HookedSubmit
						size="large"
						color="highlight"
						loading={isCreateIncomingLoading}
						disabled={disableSubmit}
						fullWidth
					>
						Confirm transfer
					</HookedSubmit>
				</DialogActions>
			</LWForm>
		</Dialog>
	);
};

export default DialogTransferDomain;
